import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { CircularProgress } from '@mui/material';
import KeywordInput from './KeywordInput';
import toast from 'react-hot-toast';
import AssetRuleFormContent from './components/AssetRuleFormContent';


const AddAssetRuleModal = ({ isOpen, onClose, onAdd, allRulesData, assetId, assetName, assetInfo, dataFrame, fetchAllData, editRuleData, initialHierarchicalData }) => {
  const [formData, setFormData] = useState({
    ruleName: '',
    description: '',
    dimension: '',
    check: '',
    checkType: '',
    keywords: [],
    arguments: '',
    threshold: '',
    thresholdType: '',
    sqlQuery: '',
    functionName: '',
    isConsistencyRule: false,
    rule_owner: '',
    remediation_action: '',
    rule_phase: '',
    filter: '',
    rule_selection_key: ''
  });
  const [availableDimensions, setAvailableDimensions] = useState([]);
  const [availableChecks, setAvailableChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editRuleId, setEditRuleId] = useState(null);
  const [hasUserStartedEditing, setHasUserStartedEditing] = useState(false);
  const [checkMapping, setCheckMapping] = useState('');
  const [ruleNameCheckMessage, setRuleNameCheckMessage] = useState('');
  const [ruleNameIsUnique, setRuleNameIsUnique] = useState(undefined);
  const [ruleNameChecking, setRuleNameChecking] = useState(false);
  const [isRuleOwnerEmailValid, setIsRuleOwnerEmailValid] = useState(true);

  const[hierarchyFormData, setHierarchyFormData] = useState({});

   useEffect(() => {
   if (!editRuleData) {
    if (Array.isArray(initialHierarchicalData) && initialHierarchicalData.length > 0) {
      const initialFormState = initialHierarchicalData.reduce((acc, currentString) => {
        acc[currentString] = '';
        return acc;
      }, {});
      setHierarchyFormData(initialFormState);
    }
  }else{
    setHierarchyFormData(editRuleData.hierarchy_config);
  }
  }, [initialHierarchicalData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHierarchyFormData(prevFormData => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (isOpen) {
      if (editRuleData) {
        setFormData({
          ruleName: editRuleData.rule_name || '',
          description: editRuleData.rule_description || '',
          dimension: editRuleData.dimension_name || '',
          check: editRuleData.check_name || '',
          keywords: editRuleData.keywords ? JSON.parse(editRuleData.keywords) : [],
          arguments: editRuleData.arguments ? (() => {
            try {
              const parsed = JSON.parse(editRuleData.arguments);
              return JSON.stringify(parsed, null, 2);
            } catch (e) {
              return editRuleData.arguments;
            }
          })() : '',
          threshold: editRuleData.threshold || '',
          thresholdType: editRuleData.threshold_type || '',
          sqlQuery: editRuleData.sql_query || '',
          functionName: editRuleData.function_name || '',
          isConsistencyRule: false,
          rule_owner: editRuleData.rule_owner || '',
          rule_phase: editRuleData.rule_phase || '',
          remediation_action: editRuleData.remediation_action || '',
          filter: editRuleData.filter || '',
          rule_selection_key: editRuleData.rule_selection_key || '',
        });
        setEditRuleId(editRuleData.attribute_rule_id || editRuleData.rule_id || null);
        setHasUserStartedEditing(false); // Reset flag when opening edit mode
        // Clear rule name validation states for edit mode
        setRuleNameCheckMessage('');
        setRuleNameIsUnique(undefined);
        setRuleNameChecking(false);
      } else {
        setFormData({ 
          ruleName: '', 
          description: '', 
          dimension: '', 
          check: '', 
          keywords: [], 
          arguments: '', 
          threshold: '', 
          thresholdType: '',
          sqlQuery: '',
          functionName: '',
          isConsistencyRule: false,
          rule_owner: '',
          remediation_action: '',
          rule_phase: '',
          filter: '',
          rule_selection_key: ''
        });
        setEditRuleId(null);
        // Clear rule name validation states for new rule
        setRuleNameCheckMessage('');
        setRuleNameIsUnique(undefined);
        setRuleNameChecking(false);
      }
    }
  }, [isOpen, editRuleData]);

  useEffect(() => {
    if (allRulesData && allRulesData.check_names) {
      setAvailableDimensions(allRulesData.check_names.map(dim => ({
        id: dim.dimension_id,
        name: dim.dimension_name,
        checks: dim.checks.asset_level
      })));
    }
  }, [allRulesData]);

  useEffect(() => {
    if (formData.dimension && availableDimensions.length > 0) {
      const dim = availableDimensions.find(d => d.name === formData.dimension);
      const checks = dim ? dim.checks : [];
      setAvailableChecks(checks);
      
      // Only clear fields if we're not in edit mode
      if (!editRuleData) {
        setFormData(prev => ({ ...prev, check: '', checkType: '', description: '' }));
        
        // Auto-select check if there's only one option
        if (checks.length === 1) {
          setFormData(prev => ({ ...prev, check: checks[0].check_name }));
        }
      }
    }
    if (!formData.dimension) {
      setAvailableChecks([]);
      // Only clear fields if we're not in edit mode
      if (!editRuleData) {
        setFormData(prev => ({ ...prev, check: '', checkType: '', description: '', ruleName: '' }));
      }
    }
  }, [formData.dimension, availableDimensions, editRuleData]);

  useEffect(() => {
    if (formData.check && availableChecks.length > 0 && !editRuleData) {
      const checkObj = availableChecks.find(c => c.check_name === formData.check);
      if (checkObj) {
        setFormData(prev => ({
          ...prev,
          checkType: checkObj.check_type || '',
          description: checkObj.rule_description || ''
        }));
        
        // Parse and format check_mapping JSON for arguments field
        if (checkObj.check_mapping) {
          try {
            const parsedMapping = JSON.parse(checkObj.check_mapping);
            const formattedJson = JSON.stringify(parsedMapping, null, 2);
            setFormData(prev => ({
              ...prev,
              arguments: formattedJson
            }));
          } catch (e) {
            // If parsing fails, just set the raw string
            setFormData(prev => ({
              ...prev,
              arguments: checkObj.check_mapping
            }));
          }
        }
        setCheckMapping(checkObj.check_mapping || '');
      } else {
        setCheckMapping('');
        setFormData(prev => ({
          ...prev,
          arguments: ''
        }));
      }
    } else if (formData.check && availableChecks.length > 0 && editRuleData) {
      // In edit mode, just set the check mapping without changing description or arguments
      const checkObj = availableChecks.find(c => c.check_name === formData.check);
      setCheckMapping(checkObj ? checkObj.check_mapping || '' : '');
      // Don't change arguments or description in edit mode - keep existing values
    } else {
      setCheckMapping('');
      // Only clear arguments if not in edit mode
      if (!editRuleData) {
        setFormData(prev => ({
          ...prev,
          arguments: ''
        }));
      }
    }
  }, [formData.check, availableChecks, editRuleData]);

  // Auto-populate rule name when check is selected
  useEffect(() => {
    if (formData.check && assetName) {
      const checkName = formData.check;
      const autoRuleName = `${assetName}_${checkName}`;
      
      // For new rules: always auto-generate
      // For edit mode: only auto-generate if user has started editing
      if (!editRuleData) {
        // New rule - auto-generate if empty or was previously auto-generated
        if (!formData.ruleName || formData.ruleName.includes('_')) {
          setFormData(prev => ({
            ...prev,
            ruleName: autoRuleName
          }));
        }
      } else {
        // Edit mode - only auto-generate if user has started editing
        if (hasUserStartedEditing) {
          setFormData(prev => ({
            ...prev,
            ruleName: autoRuleName
          }));
        }
      }
    }
  }, [formData.check, assetName, editRuleData, hasUserStartedEditing]);

  // Debounced rule name check (only for new rules, not in edit mode)
  useEffect(() => {
    if (!editRuleData && formData.ruleName && formData.ruleName.trim() !== '') {
      const timeoutId = setTimeout(() => {
        checkRuleNameUnique(formData.ruleName);
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      setRuleNameCheckMessage('');
      setRuleNameIsUnique(undefined);
    }
  }, [formData.ruleName, editRuleData]);

  // Validate Rule Owner email in real-time
  useEffect(() => {
    if (!formData.rule_owner || formData.rule_owner.trim() === '') {
      setIsRuleOwnerEmailValid(true); // Empty is considered valid for display
      return;
    }
    const ALLOWED_EMAIL_DOMAINS = ['effem.com'];
    const emailPattern = /^[^\s@]+@([^\s@]+)$/;
    const match = formData.rule_owner.match(emailPattern);
    setIsRuleOwnerEmailValid(match && ALLOWED_EMAIL_DOMAINS.includes(match[1]));
  }, [formData.rule_owner]);

  // Function to check rule name uniqueness
  const checkRuleNameUnique = async (ruleName) => {
    if (!ruleName || ruleName.trim() === '') {
      setRuleNameCheckMessage('');
      setRuleNameIsUnique(undefined);
      return;
    }

    setRuleNameChecking(true);
    try {
      const BASE_URL = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${BASE_URL}/check/${encodeURIComponent(ruleName)}`);
      
      if (response.data && typeof response.data.exists === 'boolean') {
        const exists = response.data.exists;
        setRuleNameIsUnique(!exists); // If exists is false, rule name is unique
        
        if (exists) {
          setRuleNameCheckMessage('Rule name already exists. Please choose a different name.');
        } else {
          setRuleNameCheckMessage('Rule name is available.');
        }
      } else {
        setRuleNameCheckMessage('Unable to verify rule name');
        setRuleNameIsUnique(undefined);
      }
    } catch (error) {
      console.error('Error checking rule name:', error);
      setRuleNameCheckMessage('Error checking rule name. Please try again.');
      setRuleNameIsUnique(undefined);
    } finally {
      setRuleNameChecking(false);
    }
  };

  const handleInputChange = (field, value) => {
    // If user changes dimension or check in edit mode, mark as started editing
    if (editRuleData && (field === 'dimension' || field === 'check')) {
      setHasUserStartedEditing(true);
    }
    
    // If changing rule name, clear previous check results
    if (field === 'ruleName') {
      setRuleNameCheckMessage('');
      setRuleNameIsUnique(undefined);
    }
    
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Helper function to determine if SQL Query field should be enabled
  const isSqlQueryEnabled = () => {
    if (!formData.check) return false;
    const checkName = formData.check.toLowerCase();
    return checkName === 'sql_query' || checkName === 'sql_expression' || checkName === 'is_locally_defined';
  };

  // Helper function to determine if Function Name field should be enabled
  const isFunctionNameEnabled = () => {
    if (!formData.check) return false;
    const checkName = formData.check.toLowerCase();
    return checkName === 'is_locally_defined';
  };

  // Helper function to validate JSON
  const isValidJSON = (str) => {
    if (!str || str.trim() === '') return true; // Empty string is valid
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if rule name is unique (only for new rules or if rule name was changed in edit mode)
    if (!editRuleData && ruleNameIsUnique === false) {
      setError("Rule name already exists. Please choose a different name.");
      return;
    }

    // Only validate email if rule_owner is provided (since it's optional)
    if (formData.rule_owner && formData.rule_owner.trim() !== '') {
      const ALLOWED_EMAIL_DOMAINS = ['effem.com'];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = formData.rule_owner.match(emailPattern);
      if (!match || !ALLOWED_EMAIL_DOMAINS.includes(match[1])) {
        setError(`Invalid email address - rule owner must be from: ${ALLOWED_EMAIL_DOMAINS.join(', ')}`);
        return;
      }
    }
    
    setLoading(true);
    setError(null);

    // Validate Arguments field for JSON
    if (!isValidJSON(formData.arguments)) {
      setError('Arguments field must contain valid JSON');
      setLoading(false);
      return;
    }

    try {
      const checkObj = availableChecks.find(c => c.check_name === formData.check);
      
      const BASE_URL = process.env.REACT_APP_API_URL;
      
      // Parse arguments to JSON object or keep as empty object
      let parsedArguments = {};
      if (formData.arguments && formData.arguments.trim() !== '') {
        try {
          parsedArguments = JSON.parse(formData.arguments);
        } catch (e) {
          parsedArguments = {};
        }
      }
      
      if (editRuleId) {
        // Edit payload
        const editPayload = {
          rule_id: editRuleId,
          data_asset_id: assetId || '',
          check_id: checkObj ? checkObj.check_id : '',
          rule_name: formData.ruleName,
          rule_description: formData.description,
          keywords: formData.keywords,
          threshold: formData.threshold || null,
          threshold_type: formData.thresholdType || null,
          is_active: true,
          arguments: parsedArguments,
          sql_query: formData.sqlQuery || null,
          function_name: formData.functionName || null,
          created_by: localStorage.getItem('userId') || 'user',
          last_modified_by: localStorage.getItem('userId') || 'user',
          end_date: '2999-12-01',
          check_type: "Asset Level Check",
          column: null,
          rule_owner: formData.rule_owner,
          remediation_action: formData.remediation_action,
          rule_phase: formData.rule_phase,
          filter: formData.filter,
          rule_selection_key: formData.rule_selection_key
        };
        editPayload.hierarchy_config = JSON.stringify(hierarchyFormData) || null;
        
        await axios.put(`${BASE_URL}/rules/update_rule/${editRuleId}`, editPayload);
        toast.success('Rule updated successfully!');
      } else {
        // Add new rule payload
        const addPayload = {
          data_asset_id: assetId || '',
          check_id: checkObj ? checkObj.check_id : '',
          rule_name: formData.ruleName,
          rule_description: formData.description,
          keywords: formData.keywords,
          threshold: formData.threshold || null,
          threshold_type: formData.thresholdType || null,
          is_active: true,
          arguments: parsedArguments,
          sql_query: formData.sqlQuery || null,
          function_name: formData.functionName || null,
          created_by: localStorage.getItem('userId') || 'user',
          last_modified_by: localStorage.getItem('userId') || 'user',
          end_date: '2999-12-01',
          check_type: "Asset Level Check",
          column: null,
          rule_owner: formData.rule_owner,
          remediation_action: formData.remediation_action,
          rule_phase: formData.rule_phase,
          filter: formData.filter,
          rule_selection_key: formData.rule_selection_key
        };
        addPayload.hierarchy_config = JSON.stringify(hierarchyFormData) || null;
        
        await axios.post(`${BASE_URL}/rules/add_rule`, addPayload);
        toast.success('Rule added successfully!');
      }
      
      setLoading(false);
      if(fetchAllData) fetchAllData();
      onClose();
      setFormData({ 
        ruleName: '', 
        description: '', 
        dimension: '', 
        check: '', 
        keywords: [],
        arguments: '', 
        threshold: '', 
        thresholdType: '',
        sqlQuery: '',
        functionName: '',
        rule_owner: '',
        rule_phase:'',
        remediation_action: '',
        filter: '',
        rule_selection_key: ''
      });
      setEditRuleId(null);
      setHierarchyFormData({});
      // Clear rule name validation states
      setRuleNameCheckMessage('');
      setRuleNameIsUnique(undefined);
      setRuleNameChecking(false);
    } catch (err) {
      setLoading(false);
      setError('Failed to save rule. Please try again.');
      toast.error(editRuleId ? 'Failed to update rule. Please try again.' : 'Failed to add rule. Please try again.');
    }
  };

  if (!isOpen) return null;

  // Tooltip content
  const tooltips = {
    dimension: 'Choose the data dimension that will be monitored for changes (e.g., completeness, uniqueness).',
    check: 'Select the type of check to apply (e.g., anomaly detection, range validation).',
    ruleName: 'Enter a brief description explaining the purpose and function of the rule.',
    description: 'Specify the acceptable values for the selected column to maintain data consistency.',
    arguments: 'Enter JSON arguments for the rule. Must be valid JSON format.',
    threshold: 'Set the threshold value for the rule validation.',
    thresholdType: 'Select the type of threshold (percentage, count, etc.).',
    sqlQuery: 'Enter SQL query for SQL-based checks.',
    functionName: 'Enter the function name for the rule. This field is only enabled when "is_locally_defined" check is selected.',
    ruleOwner: 'Enter the email id of the rule owner (optional).'
  };
  

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: '99999', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={60} thickness={5} color="primary" />
        </div>
      )}
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-50">
          <h2 className="text-2xl font-bold text-gray-900">
            {editRuleData ? 'Edit Rule' : 'Add New Asset Rule'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <AssetRuleFormContent
            formData={formData}
            availableDimensions={availableDimensions}
            availableChecks={availableChecks}
            hierarchyFormData={hierarchyFormData}
            ruleNameCheckMessage={ruleNameCheckMessage}
            ruleNameIsUnique={ruleNameIsUnique}
            ruleNameChecking={ruleNameChecking}
            isRuleOwnerEmailValid={isRuleOwnerEmailValid}
            loading={loading}
            editRuleData={editRuleData}
            onInputChange={handleInputChange}
            onHierarchyChange={handleChange}
            onClose={onClose}
            isSqlQueryEnabled={isSqlQueryEnabled}
            isFunctionNameEnabled={isFunctionNameEnabled}
            KeywordInputComponent={KeywordInput}
            setFormData={setFormData}
            error={error}
          />
        </form>
      </div>
    </div>
  );
};

export default AddAssetRuleModal; 